import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import SEO from '@/components/common/SEO';
import { Briefcase, MapPin, Clock, Mail, Heart, Zap, Globe } from 'lucide-react';

export default function Careers() {
  const { i18n } = useTranslation();
  const isKorean = i18n.language === 'ko';

  const benefits = [
    {
      icon: <Globe className="w-6 h-6" />,
      title: isKorean ? '완전 원격 근무' : 'Fully Remote',
      description: isKorean ? '전 세계 어디서나 일하세요' : 'Work from anywhere in the world',
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: isKorean ? '유연한 근무 시간' : 'Flexible Hours',
      description: isKorean ? '본인의 일정에 맞게 조절' : 'Set your own schedule',
    },
    {
      icon: <Heart className="w-6 h-6" />,
      title: isKorean ? '의미 있는 일' : 'Meaningful Work',
      description: isKorean ? '한인 커뮤니티에 실질적 도움' : 'Make a real impact on Korean communities',
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: isKorean ? '성장 기회' : 'Growth Opportunities',
      description: isKorean ? '빠르게 성장하는 스타트업' : 'Fast-growing startup environment',
    },
  ];

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-4xl mx-auto">
      <SEO title="Careers at KoreanHub" description="Open roles and how we work." path="/careers" />
      {/* Hero */}
      <div className="text-center mb-12">
        <div className="w-16 h-16 bg-coral-100 dark:bg-coral-900/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Briefcase className="w-8 h-8 text-coral-600" />
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
          {isKorean ? 'KoreanHub와 함께하세요' : 'Join KoreanHub'}
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
          {isKorean
            ? '전 세계 한인 커뮤니티를 연결하는 의미 있는 일에 함께할 팀원을 찾습니다'
            : 'Join our mission to connect Korean communities around the world'
          }
        </p>
      </div>

      {/* Benefits */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
          {isKorean ? '왜 KoreanHub인가요?' : 'Why KoreanHub?'}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className="bg-white dark:bg-slate-800 rounded-xl p-5 shadow-lg flex items-start gap-4"
            >
              <div className="w-12 h-12 bg-coral-100 dark:bg-coral-900/30 rounded-xl flex items-center justify-center flex-shrink-0 text-coral-600">
                {benefit.icon}
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                  {benefit.title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {benefit.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Open Positions */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
          {isKorean ? '채용 중인 포지션' : 'Open Positions'}
        </h2>

        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg overflow-hidden">
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-gray-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <Briefcase className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {isKorean ? '현재 채용 공고가 없습니다' : 'No Open Positions'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {isKorean
                ? '현재 채용 중인 포지션이 없지만, 언제든 지원서를 보내주세요!'
                : "We don't have any open positions right now, but feel free to send your resume!"
              }
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 bg-coral-500 text-white rounded-xl font-medium hover:bg-coral-600 transition-colors"
            >
              <Mail className="w-5 h-5" />
              {isKorean ? '이력서 보내기' : 'Send Your Resume'}
            </Link>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="bg-gradient-to-br from-coral-50 to-coral-100 dark:from-coral-900/20 dark:to-coral-800/10 rounded-2xl p-6 sm:p-8 text-center">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
          {isKorean ? '관심이 있으신가요?' : 'Interested?'}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          {isKorean
            ? '채용 관련 문의는 문의 양식을 이용해 주세요'
            : 'For career inquiries, please reach out through our contact form'
          }
        </p>
        <Link
          to="/contact"
          className="text-coral-600 hover:text-coral-700 font-medium"
        >
          {isKorean ? '문의하기' : 'Contact us'}
        </Link>
      </section>
    </div>
  );
}
